import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { SHORT_FORMATTED_DATE_FORMAT_FNS, SimpleListResult } from '@schooly/api';
import { Bubble, InformationIcon, TagSelect } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { format } from 'date-fns';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { SelectedCriteriaList } from '../../../../components/uikit-components/SelectedCriteriaList/SelectedCriteriaList.styled';
import { getRouteModalPathname } from '../../../../helpers/misc';
import { MessageCriteriaItem } from '../../../../context/messages/MessageContext';
import { getDisplayedCriteria } from '../../helpers';

export type MessagesModalPreviewSendToParentsProps = {
  parents: SimpleListResult[];
  parentsCriteria: MessageCriteriaItem[];
  parentsByIndividualStudent: SimpleListResult[];
  criteriaDate?: string;
};

export const MessagesModalPreviewSendToParents: FC<MessagesModalPreviewSendToParentsProps> = ({
  parents,
  parentsCriteria,
  parentsByIndividualStudent,
  criteriaDate,
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      {Boolean(parentsCriteria?.length || parentsByIndividualStudent?.length) && (
        <SelectedCriteriaList>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {getDisplayedCriteria({
              criteria: parentsCriteria,
              formatMessage,
              children: (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="h4" pr={1.75}>
                    <FormattedMessage id="messages-ParentsOfStudentsByCriteria" />
                  </Typography>

                  {!!criteriaDate && (
                    <Stack direction="row" alignItems="center" spacing={0.5} pr={1}>
                      <TagSelect
                        label={
                          <Typography variant="h3" color={(theme) => theme.palette.common.grey2}>
                            {format(
                              newDateTimezoneOffset(criteriaDate),
                              SHORT_FORMATTED_DATE_FORMAT_FNS,
                            )}
                          </Typography>
                        }
                        sx={(theme) => ({
                          '&.MuiChip-root': {
                            backgroundColor: theme.palette.background.paper,
                          },
                        })}
                      />

                      <Tooltip
                        title={formatMessage({ id: 'messages-ParentsOfStudentsPreviewTooltip' })}
                        placement="top"
                      >
                        <IconButton inverse sx={(theme) => ({ color: `inherit` })}>
                          <InformationIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              ),
            })}
          </Stack>
          {
            /* Parents by individual students */
            Boolean(parentsByIndividualStudent?.length) && (
              <Stack direction="row" mt={1} alignItems="baseline" gap={1} flexWrap="wrap">
                <Typography variant="h4">
                  <FormattedMessage id="messages-OfStudentsIndividually" />
                </Typography>

                {parentsByIndividualStudent.map((student, index, arr) => (
                  <React.Fragment key={student.user_id}>
                    <Link to={getRouteModalPathname('student', student)}>
                      <Bubble
                        size="medium"
                        tone="gray"
                        onClick={() => getRouteModalPathname('student', student)}
                        key={student.user_id}
                        label={getUserFullName(student)}
                      />
                    </Link>
                    {index < arr.length - 1 && (
                      <Typography variant="h3" color="common.grey2">
                        ,
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            )
          }
        </SelectedCriteriaList>
      )}

      {
        /* Individual parents */
        Boolean(parents?.length) && (
          <Box>
            <Typography variant="h4">
              <FormattedMessage id="messages-OfStudentsIndividually" />
            </Typography>
            <Box>
              {parents.map((parent) => (
                <Bubble
                  size="medium"
                  tone="gray"
                  key={parent.user_id}
                  label={getUserFullName(parent)}
                />
              ))}
            </Box>
          </Box>
        )
      }
    </>
  );
};
